import React from 'react';
import { useTranslation } from 'react-i18next';

// Components
import MultiSelectDropdown from '../../inputs/DropDown/MultiSelectDropdown';
import InputText from '../../inputs/InputText';
import { Typography } from '../../display/Typography';

// Types
import { type RouteRoadRangeFormValue } from '../../../pages/main/surveillanceRoute/NewRoute/schema';

// Styles
import { FormField, PKRow } from './styles';

// ═══════════════════════════════════════════════════════════════════
// CARRETERAS + UN RANGO DE PK POR CARRETERA
//
// Extraído de `UbicacioTab` (plantillas de recorrido, 2026-08-16) porque lo
// necesitan DOS pantallas: el asistente de recorrido y el modal de Gestión que
// define las plantillas. Dos copias de «cada PK se valida contra los extremos
// de SU carretera» divergirían en el primer arreglo, y esa regla es justo la
// que hace falta cuando una troncal y su ramal comparten kilometraje.
//
// Va en DOS piezas y no en una: en el asistente el desplegable de carreteras
// comparte fila con el de concesión, y las filas de PK van más abajo, después
// del selector de equipo. Un solo componente que pintara ambos obligaría a
// mover el desplegable de sitio. El modal de Gestión, que no tiene nada en
// medio, usa el `RoadRangesFields` que los compone.
//
// Las props NO hablan de react-hook-form a propósito: cada llamante tiene su
// propio formulario, con su propio tipo de valores, y lo único que comparten es
// esta pinta. El llamante resuelve sus `register` y sus errores y los pasa ya
// hechos.
// ═══════════════════════════════════════════════════════════════════

export interface RoadSelectFieldProps {
  roadOptions: Array<{ label: string; value: string }>;
  roadIds: string[];
  onRoadsChange: (roadIds: string[]) => void | Promise<void>;
  roadsError?: string;
  /** Para poder limpiar la selección desde fuera (lo usa el asistente al
   *  cambiar de concesión). Sin tipar, como ya lo pasaba `UbicacioTab`: el
   *  handle imperativo del desplegable declara `| null` y no casa con el
   *  `LegacyRef` que espera su `forwardRef`. */
  dropdownRef?: React.MutableRefObject<any>;
}

/** El desplegable múltiple de carreteras. */
export function RoadSelectField({
  roadOptions,
  roadIds,
  onRoadsChange,
  roadsError,
  dropdownRef,
}: RoadSelectFieldProps): JSX.Element {
  const { t } = useTranslation(['pages', 'common']);

  return (
    <MultiSelectDropdown
      ref={dropdownRef}
      labelText={t('pages:surveillance.road')}
      // Placeholder CORTO (reporte en tablet 2026-08-17): con «Selecciona una
      // carretera» el desplegable se partía en dos líneas y crecía al doble de
      // alto. Es la misma clave genérica que usa el selector de Equipo del
      // asistente — «Selecciona» / «Select» — y cabe en una línea.
      placeholder={t('common:placeholders.select')}
      options={roadOptions}
      selectedValues={roadIds ?? []}
      onChangeSelected={onRoadsChange}
      inputSize="M"
      typeDropDown="Default"
      borderError={!!roadsError}
      error={roadsError}
    />
  );
}

export interface RoadRangeRowsProps {
  roadOptions: Array<{ label: string; value: string }>;
  roadRanges: RouteRoadRangeFormValue[];
  /** El `register` de la fila `index`, ya resuelto por el formulario llamante. */
  registerPkField: (
    index: number,
    field: 'pkInit' | 'pkEnd'
  ) => Record<string, unknown>;
  errorFor: (index: number, field: 'pkInit' | 'pkEnd') => string | undefined;
}

/** Una fila por carretera: sus extremos (read-only) y sus PK editables. */
export function RoadRangeRows({
  roadOptions,
  roadRanges,
  registerPkField,
  errorFor,
}: RoadRangeRowsProps): JSX.Element {
  const { t } = useTranslation(['pages', 'common']);

  return (
    <>
      {/* Con una sola carretera se ve exactamente igual que antes de
          multi-carretera; el nombre solo aparece cuando hay más de una, que es
          cuando hace falta para saber cuál es cuál. */}
      {(roadRanges ?? []).map((row, index) => {
        const roadName = roadOptions.find((o) => o.value === row.roadId)?.label;

        return (
          <React.Fragment key={row.roadId}>
            {(roadRanges?.length ?? 0) > 1 && roadName && (
              // El nombre de la vía manda sobre las dos filas de PK que le
              // siguen: es una cabecera, no otra etiqueta del formulario
              // (2026-08-17).
              <Typography component="h3" variant="semiBold" size="M">
                {roadName}
              </Typography>
            )}

            {/* Extremos de la vía (read-only, del catálogo) */}
            {row.pkInitRoad && (
              <PKRow>
                <FormField>
                  <InputText
                    typeInput="normal"
                    label={t('pages:surveillance.inici')}
                    value={row.pkInitRoad}
                    inputSize="S"
                    disabled
                  />
                </FormField>
                <FormField>
                  <InputText
                    typeInput="normal"
                    label={t('pages:surveillance.final')}
                    value={row.pkEndRoad}
                    inputSize="S"
                    disabled
                  />
                </FormField>
              </PKRow>
            )}

            {/* PK editables (register + patrón de error de LocationForm) */}
            <PKRow>
              <FormField>
                <InputText
                  {...registerPkField(index, 'pkInit')}
                  typeInput="normal"
                  label={t('pages:surveillance.pkInit')}
                  placeholder={t('common:pkExample')}
                  inputSize="S"
                  borderError={!!errorFor(index, 'pkInit')}
                  error={errorFor(index, 'pkInit')}
                />
              </FormField>
              <FormField>
                <InputText
                  {...registerPkField(index, 'pkEnd')}
                  typeInput="normal"
                  label={t('pages:surveillance.pkEnd')}
                  placeholder={t('common:pkExample')}
                  inputSize="S"
                  borderError={!!errorFor(index, 'pkEnd')}
                  error={errorFor(index, 'pkEnd')}
                />
              </FormField>
            </PKRow>
          </React.Fragment>
        );
      })}
    </>
  );
}

export type RoadRangesFieldsProps = RoadSelectFieldProps & RoadRangeRowsProps;

/**
 * Las dos piezas juntas, para quien no tenga nada en medio (el modal de
 * Gestión).
 */
export default function RoadRangesFields(
  props: RoadRangesFieldsProps
): JSX.Element {
  return (
    <>
      <FormField>
        <RoadSelectField {...props} />
      </FormField>
      <RoadRangeRows {...props} />
    </>
  );
}
